/**
 * Determines whether the current page should enable ticker/balance polling.
 * Polling is restricted to dashboard, accounts, and exchange pages to avoid
 * unnecessary network requests on settings, support, recipients, etc.
 *
 * With next-intl's `localePrefix: "as-needed"` and a single default locale,
 * pathnames are NOT locale-prefixed (e.g., "/accounts" not "/en-GB/accounts").
 * We handle both prefixed and non-prefixed paths for robustness.
 */

const LOCALE_PATTERN = /^[a-z]{2}(-[A-Z]{2})?$/;

export function shouldPollOnPage(pathname: string): boolean {
  if (!pathname) return false;
  const segments = pathname.split("/");
  // segments[0] is always "" (from the leading slash).
  // segments[1] may be a locale tag ("en-GB") or the first route segment.
  const firstSegment = segments[1] ?? "";
  const routeSegments = LOCALE_PATTERN.test(firstSegment)
    ? segments.slice(2)
    : segments.slice(1);
  const routePath = "/" + routeSegments.join("/");

  // Dashboard
  if (routePath === "/") return true;

  // Accounts list and detail
  if (routePath === "/accounts") return true;
  if (/^\/accounts\/[^/]+$/.test(routePath)) return true;

  // Exchange pages — prices and balances are actively displayed in the
  // currency picker and used for sorting by balance value.
  if (routePath === "/exchange") return true;
  if (/^\/exchange\/[^/]+$/.test(routePath)) return true;

  return false;
}
